import { AmberHoc, AmberHocProps } from '../../../util';
import {
  CreateGrainBinSystemMutationVariables as Variables,
  GetAccountDocument,
  GetAccountQuery,
  GetAccountQueryVariables,
  GetGrainBinDocument,
  GetGrainBinQuery,
  GetGrainBinQueryVariables,
  GrainBinFragmentFragment as Result,
  withCreateGrainBinSystem as HOC,
} from '../__generated';
import { GraphQLErrors } from './error';

export type WithCreateGrainBinSystemHocChildProps = {
  createGrainBinSystem: (variables: Variables) => Promise<Result>;
};

export const withCreateGrainBinSystemHoc: AmberHoc<{}, WithCreateGrainBinSystemHocChildProps> = (
  component
) =>
  HOC<
    AmberHocProps<{}, WithCreateGrainBinSystemHocChildProps, typeof component>,
    WithCreateGrainBinSystemHocChildProps
  >({
    options: { errorPolicy: 'all' },
    props: (props) => {
      const { mutate } = props;
      return {
        createGrainBinSystem: async (variables) => {
          if (!mutate) {
            throw new Error('Unexpected UI error');
          }
          const { account_id } = variables;
          const result = await mutate({
            variables,
            update: (proxy, { data, errors }) => {
              if (errors || !data) {
                return;
              }
              const { createGrainBinSystem: result } = data;
              const { grain_bin_id, __typename, grain_bin } = result;

              try {
                proxy.writeQuery<GetGrainBinQuery, GetGrainBinQueryVariables>({
                  query: GetGrainBinDocument,
                  data: { grain_bin, __typename: 'Query' },
                  variables: { grain_bin_id },
                });
              } catch (err) {
                // no-op
              }

              try {
                const cached = proxy.readQuery<GetAccountQuery, GetAccountQueryVariables>({
                  query: GetAccountDocument,
                  variables: { account_id },
                });
                if (!cached) {
                  return;
                }
                const { account } = cached;
                if (!account) {
                  return;
                }
                proxy.writeQuery<GetAccountQuery, GetAccountQueryVariables>({
                  query: GetAccountDocument,
                  data: {
                    __typename: 'Query',
                    account: {
                      ...account,
                      grain_bin_links: [
                        ...account.grain_bin_links,
                        {
                          __typename,
                          grain_bin_id,
                          grain_bin: {
                            grain_bin_id,
                            __typename: grain_bin.__typename,
                            alias: grain_bin.bin_name,
                            location: grain_bin.location,
                            grain_type: grain_bin.grain_type,
                            archived: false,
                            fan_controllers: [], // not sure why but setting this bombs grain_bin.fan_controllers,
                          },
                        },
                      ],
                    },
                  },
                  variables: { account_id },
                });
              } catch (err) {
                // no-op
              }

              // shouldnt need to update hubs and fan controllers in the cache since there shouldnt be any preexisting data in the cache
            },
          });
          if (!result) {
            throw new Error('Unexpected server response');
          }
          const { data, errors } = result;
          if (errors) {
            throw new GraphQLErrors(errors);
          }
          if (!data) {
            throw new Error('Unexpected server response');
          }
          return data.createGrainBinSystem.grain_bin;
        },
      };
    },
  })(component as any);
